import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import ActivityLogService from '../../services/activityLogService';
import Button from '../common/Button';
import './ActivityLogs.css';

const ActivityLogs = () => {
  const auth = useAuth();
  
  // Set today's date as default
  const getTodayDate = () => {
    const today = new Date();
    return today.getFullYear() + '-' + 
           String(today.getMonth() + 1).padStart(2, '0') + '-' + 
           String(today.getDate()).padStart(2, '0');
  };
  
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedEntityType, setSelectedEntityType] = useState('all');
  const [activityLogs, setActivityLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    lastEvaluatedKey: null,
    hasMore: false
  });

  // Use ref to store current pagination to avoid dependency cycles
  const paginationRef = useRef(pagination);
  
  // Update ref when pagination state changes
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  // Action and entity type options for filters
  const actionOptions = [
    { value: 'all', label: 'Todas las acciones' },
    { value: 'CREATE', label: 'Crear' },
    { value: 'UPDATE', label: 'Actualizar' },
    { value: 'DELETE', label: 'Eliminar' },
    { value: 'UPDATE_STATUS', label: 'Cambiar estado' }
  ];

  const entityTypeOptions = [
    { value: 'all', label: 'Todas las entidades' },
    { value: 'patient', label: 'Pacientes' },
    { value: 'appointment', label: 'Citas' },
    { value: 'user', label: 'Usuarios' },
    { value: 'organization', label: 'Organizaciones' },
    { value: 'procedure', label: 'Procedimientos' },
    { value: 'supply', label: 'Suministros' },
    { value: 'payment', label: 'Pagos' },
    { value: 'farmaco', label: 'Fármacos' },
    { value: 'insurance', label: 'Seguros' }
  ];

  // Load activity logs
  const loadActivityLogs = useCallback(async (loadMore = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use ref to get current pagination state to avoid dependency issues
      const lastKey = loadMore ? paginationRef.current.lastEvaluatedKey : null;
      const response = await ActivityLogService.getActivityLogs(
        auth.user?.access_token,
        50,
        lastKey
      );

      const newLogs = response.data || [];
      console.log('ActivityLogs - Raw API response:', response);
      console.log('ActivityLogs - Parsed logs:', newLogs);
      
      if (loadMore) {
        setActivityLogs(prev => [...prev, ...newLogs]);
      } else {
        setActivityLogs(newLogs);
      }

      setPagination({
        count: response.pagination?.count || newLogs.length,
        lastEvaluatedKey: response.pagination?.lastEvaluatedKey || null,
        hasMore: response.pagination?.hasMore || false
      });

    } catch (error) {
      console.error('Error loading activity logs:', error);
      setError('Error al cargar los registros de actividad');
    } finally {
      setIsLoading(false);
    }
  }, [auth.user?.access_token]);

  // Filter logs based on selected filters (manual trigger only)
  const filterLogs = useCallback(() => {
    let filtered = [...activityLogs];
    console.log('ActivityLogs - Original logs before filtering:', activityLogs.length);
    console.log('ActivityLogs - Filter criteria:', { selectedDate, selectedAction, selectedEntityType });

    // Filter by date
    if (selectedDate) {
      console.log('ActivityLogs - Filtering by date:', selectedDate);
      filtered = filtered.filter(log => {
        // Convert log timestamp to local timezone for comparison
        const logDate = new Date(log.timestamp);
        
        // Get the date in local timezone (YYYY-MM-DD format)
        const logDateLocal = logDate.getFullYear() + '-' + 
                           String(logDate.getMonth() + 1).padStart(2, '0') + '-' + 
                           String(logDate.getDate()).padStart(2, '0');
        
        const match = logDateLocal === selectedDate;
        console.log('ActivityLogs - Comparing:', { 
          logTimestamp: log.timestamp, 
          logDateLocal, 
          selectedDate, 
          match 
        });
        return match;
      });
      console.log('ActivityLogs - After date filter:', filtered.length);
    }

    // Filter by action
    if (selectedAction !== 'all') {
      console.log('ActivityLogs - Filtering by action:', selectedAction);
      filtered = filtered.filter(log => log.action === selectedAction);
      console.log('ActivityLogs - After action filter:', filtered.length);
    }

    // Filter by entity type
    if (selectedEntityType !== 'all') {
      console.log('ActivityLogs - Filtering by entity type:', selectedEntityType);
      filtered = filtered.filter(log => log.entityType === selectedEntityType);
      console.log('ActivityLogs - After entity type filter:', filtered.length);
    }

    console.log('ActivityLogs - Final filtered logs:', filtered.length);
    setFilteredLogs(filtered);
  }, [activityLogs, selectedDate, selectedAction, selectedEntityType]);

  // Load initial data only once when component mounts and user is authenticated
  useEffect(() => {
    if (auth.user?.access_token) {
      loadActivityLogs();
    }
  }, [auth.user?.access_token, loadActivityLogs]);

  // Apply filters initially when logs are loaded
  useEffect(() => {
    filterLogs();
  }, [filterLogs]);

  // Load more logs
  const handleLoadMore = () => {
    if (pagination.hasMore && !isLoading) {
      loadActivityLogs(true);
    }
  };

  // Refresh logs and apply filters
  const handleRefresh = async () => {
    setActivityLogs([]);
    setPagination({
      count: 0,
      lastEvaluatedKey: null,
      hasMore: false
    });
    await loadActivityLogs();
    // Apply filters after loading
    setTimeout(() => filterLogs(), 100);
  };

  // Format activity log for display
  const formatActivityLog = (log) => {
    return ActivityLogService.formatActivityLogForDisplay(log);
  };

  // Get activity summary for the selected date
  const getActivitySummary = () => {
    const totalActivities = filteredLogs.length;
    const actionCounts = filteredLogs.reduce((acc, log) => {
      acc[log.action] = (acc[log.action] || 0) + 1;
      return acc;
    }, {});

    const entityCounts = filteredLogs.reduce((acc, log) => {
      acc[log.entityType] = (acc[log.entityType] || 0) + 1;
      return acc;
    }, {});

    return {
      totalActivities,
      actionCounts,
      entityCounts
    };
  };

  const summary = getActivitySummary();

  return (
    <div className="activity-logs">
      <div className="reports-header">
        <h1>Registro de Actividad</h1>
        
        {/* Controls */}
        <div className="reports-controls">
          <div className="control-group">
            <label>Fecha:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="control-group">
            <label>Acción:</label>
            <select 
              value={selectedAction} 
              onChange={(e) => setSelectedAction(e.target.value)}
              className="form-select"
            >
              {actionOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label>Tipo de Entidad:</label>
            <select 
              value={selectedEntityType} 
              onChange={(e) => setSelectedEntityType(e.target.value)}
              className="form-select"
            >
              {entityTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <Button 
            onClick={handleRefresh}
            disabled={isLoading}
            className="refresh-btn"
            icon="fas fa-search"
          >
            Buscar
          </Button>
        </div>
      </div>

      {/* Loading and Error States */}
      {isLoading && activityLogs.length === 0 && (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando registros de actividad...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      )}

      {/* Content */}
      {!isLoading && !error && activityLogs.length > 0 && (
        <div className="reports-content">
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <h3>Total de Actividades</h3>
              <p className="count">{summary.totalActivities}</p>
              <small>en la fecha seleccionada</small>
            </div>
            <div className="summary-card">
              <h3>Creaciones</h3>
              <p className="count create">{summary.actionCounts.CREATE || 0}</p>
              <small>nuevos registros</small>
            </div>
            <div className="summary-card">
              <h3>Actualizaciones</h3>
              <p className="count update">{summary.actionCounts.UPDATE || 0}</p>
              <small>modificaciones</small>
            </div>
            <div className="summary-card">
              <h3>Eliminaciones</h3>
              <p className="count delete">{(summary.actionCounts.DELETE || 0) + (summary.actionCounts.SOFT_DELETE || 0) + (summary.actionCounts.HARD_DELETE || 0)}</p>
              <small>registros eliminados</small>
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="activity-timeline-container">
            <h3>Cronología de Actividades</h3>
            
            {filteredLogs.length > 0 ? (
              <div className="activity-timeline">
                {filteredLogs.map((log) => {
                  const formattedLog = formatActivityLog(log);
                  return (
                    <div key={log.id} className="timeline-item">
                      <div className="timeline-marker">
                        <i className={formattedLog.icon}></i>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <h4>{formattedLog.description}</h4>
                          <span className="timeline-time">
                            {new Date(log.timestamp).toLocaleString('es-ES')}
                          </span>
                        </div>
                        <div className="timeline-details">
                          <div className="detail-item">
                            <strong>Acción:</strong> 
                            <span className={`action-badge ${formattedLog.action.toLowerCase()}`}>
                              {formattedLog.actionDisplayName}
                            </span>
                          </div>
                          <div className="detail-item">
                            <strong>Entidad:</strong> {formattedLog.entityDisplayName}
                          </div>
                          {log.userId && (
                            <div className="detail-item">
                              <strong>Usuario:</strong> {log.userId}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-activity">
                <i className="fas fa-info-circle"></i>
                <p>No se encontraron actividades para los filtros seleccionados</p>
              </div>
            )}
          </div>

          {/* Load More Button */}
          {pagination.hasMore && (
            <div className="load-more-container">
              <Button
                onClick={handleLoadMore}
                disabled={isLoading}
                variant="outline"
                className="load-more-btn"
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Cargando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-chevron-down"></i>
                    Cargar más actividades
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* No Data State */}
      {!isLoading && !error && activityLogs.length === 0 && (
        <div className="no-data-state">
          <i className="fas fa-clipboard-list"></i>
          <h3>No hay registros de actividad</h3>
          <p>No se encontraron actividades en el sistema.</p>
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
